import React, { useCallback, useState } from 'react'
import { Modal, Spinner } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useDropzone } from 'react-dropzone'
import { AppDispatch } from '@store/index'
import { fetchFolderById, uploadFileAction } from '@store/actions/folder.action'
import { clearFolderError } from '@store/reducers/folder.reducer'
import { useAppSelector } from '@store/hooks'
import { formatBytesToMB } from '@utils/imageViewer.utils'
import { IFolder } from '@dataTypes/folder.types'
import {
  ModalHeaderWrapper,
  ModalTitle,
  ModalBodyWrapper,
  UploadArea,
  UploadedFileSample,
  FileName,
  FileSize,
  BottomBar,
  DicomUploadIcon,
} from './DicomUploader.styles'
import Button from '@components/common/Button/Button.component'

interface Props {
  show: boolean
  onClose: () => void
  selectedFolder: IFolder | null
}

const DicomUploaderModal: React.FC<Props> = ({
  show,
  onClose,
  selectedFolder,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { selectedPatient } = useAppSelector((state) => state.patient)
  const activeClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )
  const { loading: folderLoading, error: folderError } = useAppSelector(
    (state) => state.folder,
  )

  // const [patientId, setPatientId] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Reset selectedFile when modal is closed
  const handleClose = (): void => {
    setSelectedFile(null)
    setError(null)
    dispatch(clearFolderError())
    onClose()
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0])
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/dicom': ['.dcm'],
      'application/octet-stream': ['.dcm'],
    },
    multiple: false,
  })

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSubmit = async () => {
    if (!selectedFile || !selectedPatient || !activeClinicId) {
      setError('Please select a file and patient')
      return
    }

    if (!selectedFolder) {
      setError('Please select a folder')
      return
    }

    setError(null)

    try {
      await dispatch(
        uploadFileAction({
          file: selectedFile,
          patientId: selectedPatient.patient_id,
          clinicId: activeClinicId,
          folderId: selectedFolder.folder_id,
        }),
      ).unwrap()

      // Upload successful, close modal and refresh folder
      handleClose()
      dispatch(fetchFolderById(selectedFolder.folder_id))
    } catch (uploadError: unknown) {
      // setError(
      //   uploadError instanceof Error
      //     ? uploadError.message
      //     : 'Upload failed. Please try again.',
      // )
    }
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop="static"
      keyboard={false}
    >
      <ModalHeaderWrapper>
        <ModalTitle>Upload file</ModalTitle>
      </ModalHeaderWrapper>
      <ModalBodyWrapper>
        <div
          style={{
            marginBottom: 8,
            color: '#212121',
            fontSize: 16,
            fontWeight: 400,
          }}
        >
          {selectedFolder && (
            <div
              style={{ fontSize: 14, color: 'var(--blue-600)', marginTop: 4 }}
            >
              Uploading to folder: <strong>{selectedFolder.folder_name}</strong>
            </div>
          )}
        </div>
        <UploadArea {...getRootProps()} isDragActive={isDragActive}>
          <input {...getInputProps()} />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <DicomUploadIcon>
              <i className="mgc mgc_file_upload_fill"></i>
            </DicomUploadIcon>
            <div
              style={{
                margin: '4px 0 0 0',
                fontSize: 16,
                color: 'var(--gray-500)',
              }}
            >
              Drag & drop file here or{' '}
              <span
                style={{ color: '#1976d2', fontWeight: 500, cursor: 'pointer' }}
              >
                choose file
              </span>
            </div>
            <div
              style={{ fontSize: 14, color: 'var(--gray-400)', marginTop: 2 }}
            >
              Max upload 100 MB
            </div>
          </div>
        </UploadArea>
        {selectedFile && (
          <UploadedFileSample>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <svg
                width="24"
                height="24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: 8 }}
              >
                <rect width="24" height="24" rx="4" fill="#1976d2" />
              </svg>
              <FileName>{selectedFile.name}</FileName>
              <FileSize>{formatBytesToMB(selectedFile.size)} MB</FileSize>
            </div>
            <span
              style={{ cursor: 'pointer', color: '#757575', fontSize: 20 }}
              onClick={() => setSelectedFile(null)}
            >
              &times;
            </span>
          </UploadedFileSample>
        )}
        {/* {(error || folderError) && (
          <Alert variant="danger" style={{ marginTop: 16 }}>
            {error || folderError}
          </Alert>
        )} */}
      </ModalBodyWrapper>
      <BottomBar className="d-flex gap-2">
        <Button
          className="button button-secondary"
          onClick={handleClose}
          disabled={folderLoading}
          type="button"
        >
          Cancel
        </Button>
        <Button
          className="button button-primary"
          onClick={handleSubmit}
          disabled={folderLoading || !selectedFile}
          type="button"
        >
          {folderLoading ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                style={{ marginRight: 8 }}
              />
              Uploading...
            </>
          ) : (
            'Upload'
          )}
        </Button>
      </BottomBar>
    </Modal>
  )
}

export default DicomUploaderModal
